import React, { useState, useCallback } from 'react';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert relative URLs to absolute
const getAbsoluteUrl = (url) => {
  if (!url) return '';
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative URL starting with /, prepend the base URL
  if (url.startsWith('/')) {
    return `${BASE_URL}${url}`;
  }
  return url;
};

export const ImageUploader = ({ value, onChange, label, token, uploadEndpoint = '/upload' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max for general, 2MB for profile pictures)
    const maxSize = uploadEndpoint.includes('profile') ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Image must be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}${uploadEndpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      // Use the URL from the response
      onChange(response.data.url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    uploadFile(file);
  }, [token, onChange]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    uploadFile(file);
  };

  const handleClear = () => {
    onChange('');
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium text-stone-700">{label}</label>}
      
      {/* Preview or Drop Zone */}
      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
          <img 
            src={getAbsoluteUrl(value)} 
            alt="Preview" 
            className="w-full h-40 object-cover"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f5f5f4" width="100" height="100"/><text x="50%" y="50%" fill="%23a8a29e" text-anchor="middle" dy=".3em" font-size="12">Invalid URL</text></svg>';
            }}
          />
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-white/90 rounded-full shadow hover:bg-white transition-colors"
          >
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors duration-200
            ${isDragging 
              ? 'border-primary bg-primary/5' 
              : 'border-stone-300 hover:border-primary/50 hover:bg-stone-50'
            }
            ${isUploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isUploading}
          />
          
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-stone-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-700">
                  Drop an image here or click to upload
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  JPEG, PNG, GIF, WebP up to 5MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* URL Input (alternative) */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-stone-500">Or enter URL:</span>
        <input
          type="text"
          value={value || ''}
          onChange={handleUrlChange}
          placeholder="https://example.com/image.jpg"
          className="flex-1 text-xs px-2 py-1 border border-stone-200 rounded bg-stone-50 focus:outline-none focus:border-primary"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
